import { StateCreator, StoreMutatorIdentifier } from "zustand";

type TLoggerImplOptions = {
  name?: string;
  enabled?: boolean;
};

type TLogger = <
  T,
  Mps extends [StoreMutatorIdentifier, unknown][] = [],
  Mcs extends [StoreMutatorIdentifier, unknown][] = [],
>(
  f: StateCreator<T, Mps, Mcs>,
  options?: TLoggerImplOptions
) => StateCreator<T, Mps, Mcs>;

type TLoggerImpl = <T>(f: StateCreator<T, [], []>, options?: TLoggerImplOptions) => StateCreator<T, [], []>;

const REDACTED_KEYS = new Set(["accessToken", "resetToken", "profile", "verificationRequestId"]);

function sanitizeState(state: unknown): unknown {
  if (!state || typeof state !== "object") return state;
  return Object.fromEntries(
    Object.entries(state as Record<string, unknown>).map(([k, v]) => [
      k,
      REDACTED_KEYS.has(k) ? "[redacted]" : typeof v === "function" ? "[fn]" : v,
    ])
  );
}

const loggerImpl: TLoggerImpl =
  (config, options = {}) =>
  (set, get, store) => {
    const loggedSet: typeof set = (...args) => {
      set(...(args as Parameters<typeof set>));
      if (options.enabled) {
        console.info("[store] State updated via set", {
          storeName: options.name,
          state: sanitizeState(get()),
        });
      }
    };
    const setState = store.setState;

    store.setState = (...args) => {
      setState(...(args as Parameters<typeof setState>));
      if (options.enabled) {
        console.info("[store] State updated via setState", {
          storeName: options.name,
          state: sanitizeState(store.getState()),
        });
      }
    };

    return config(loggedSet, get, store);
  };

export const logger = loggerImpl as TLogger;
